import { UseFormReturn, useController } from 'react-hook-form'
import { FormControl, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import CtaEditor from '@/components/shared/CtaEditor'
import { DraggableFieldArray } from '@/components/shared/DraggableFieldArray'
import { LinkRowEditor } from '@/components/shared/LinkRowEditor'
import { blankLinkRow, LinkRowFormValues } from '@/lib/linkRow'
import { CategoryTreeSelect } from '@/containers/Categories/components/CategoryTreeSelect'
import { PageParentSelect } from '@/containers/Pages/components/PageParentSelect'
import { NavItemType, PanelSource } from '../types'

interface NavigationItemEditorProps {
  form: UseFormReturn<any>
  index: number
  languageId: number
  allLanguages: string[]
}

function linkRowTitle(link: LinkRowFormValues, index: number, languageId: number): string {
  const label =
    link.profiles[languageId]?.label?.trim() ||
    link.profiles.find(p => p.label?.trim())?.label
  return label?.trim() ? label : `Link #${index + 1}`
}

/**
 * The curated link list, shared by `dropdown` items and manual-source
 * `mega_menu` panels — identical editors over the same `links` array. The
 * only thing that differs is the `droppableId`, which must be unique among
 * every `Droppable` under the page's single `DragDropContext` (see
 * `NavigationSettings`'s `handleDragEnd`).
 */
function NavLinkList({
  form,
  base,
  droppableId,
  languageId,
  allLanguages,
}: {
  form: UseFormReturn<any>
  base: string
  droppableId: string
  languageId: number
  allLanguages: string[]
}) {
  return (
    <div>
      <p className="text-sm font-medium mb-2">Links</p>
      <DraggableFieldArray
        form={form}
        name={`${base}.links`}
        droppableId={droppableId}
        onAdd={() => blankLinkRow(allLanguages)}
        itemTitle={(link, linkIndex) => linkRowTitle(link, linkIndex, languageId)}
        renderItem={linkIndex => (
          <LinkRowEditor
            form={form}
            basePath={`${base}.links.${linkIndex}`}
            languageId={languageId}
          />
        )}
        addLabel="Add link"
        emptyText="No links yet."
      />
    </div>
  )
}

export function NavigationItemEditor({ form, index, languageId, allLanguages }: NavigationItemEditorProps) {
  const { control } = form
  const base = `items.${index}`
  const type: NavItemType = form.watch(`${base}.type`)
  const panelSource: PanelSource = form.watch(`${base}.panel_source`)

  const { field: panelRootCategoryField } = useController({
    control,
    name: `${base}.panel_root_category_id`,
  })

  // One-directional: switching AWAY FROM a type clears that type's
  // own-only fields, because the backend silently nulls/ignores whichever
  // fields aren't relevant to the newly-selected type on save — the form
  // must not show a value that would actually be dropped. Switching a
  // second time does NOT restore anything (nothing to restore) and must
  // NOT touch the item's own link target (`link_type`/`external_url`/etc.),
  // since the backend only ever nulls/ignores fields OUTSIDE the active
  // type, never those. Exception: `links` is shared by `dropdown` items and
  // manual-source `mega_menu` panels, so it is preserved across type switches
  // and cleared only when switching to a plain `link`.
  const handleTypeChange = (next: NavItemType) => {
    form.setValue(`${base}.type`, next, { shouldDirty: true })
    if (next !== 'mega_menu') {
      form.setValue(`${base}.panel_root_category_id`, null, { shouldDirty: true })
      form.setValue(`${base}.panel_root_page_id`, null, { shouldDirty: true })
      form.setValue(`${base}.featured_page_id`, null, { shouldDirty: true })
    }
    // `links` are now edited by BOTH `dropdown` items and manual-source
    // `mega_menu` panels, so they are only cleared when switching to a plain
    // link — the one type that can never own a curated list. The old rule
    // (clear on every switch away from `dropdown`) silently destroyed a
    // manual panel's entire list on a Mega -> Dropdown -> Mega round trip.
    if (next === 'link') {
      form.setValue(`${base}.links`, [], { shouldDirty: true })
    }
  }

  // Mutually exclusive: only one of `panel_root_category_id` /
  // `panel_root_page_id` can drive the panel. The live rule is source-driven:
  // whichever `panel_source` is selected decides which scope field survives.
  // Clearing the fields not owned by the selected source keeps the form's
  // state (and thus the next save) from ever holding a value the backend
  // would drop. The "page wins if both are sent" behavior is now only a
  // legacy-compatibility fallback for payloads that omit `panel_source`.
  const handlePanelSourceChange = (next: PanelSource) => {
    form.setValue(`${base}.panel_source`, next, { shouldDirty: true })
    // Clear whichever scope fields the newly-selected source does not own, so
    // the form can never hold a value the backend would drop. `manual` owns
    // neither id — it is driven entirely by `links`, which are deliberately
    // NOT cleared here so switching source and back doesn't lose the list.
    if (next !== 'categories') {
      form.setValue(`${base}.panel_root_category_id`, null, { shouldDirty: true })
    }
    if (next !== 'page') {
      form.setValue(`${base}.panel_root_page_id`, null, { shouldDirty: true })
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 items-start gap-4">
        <Label className="text-right pt-2">Type</Label>
        <div className="col-span-3 flex gap-4 pt-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={type === 'link'}
              onChange={() => handleTypeChange('link')}
            />
            <span className="text-sm">Link</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={type === 'dropdown'}
              onChange={() => handleTypeChange('dropdown')}
            />
            <span className="text-sm">Dropdown</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={type === 'mega_menu'}
              onChange={() => handleTypeChange('mega_menu')}
            />
            <span className="text-sm">Mega menu</span>
          </label>
        </div>
      </div>

      {type === 'link' ? (
        // CtaEditor's own "CTA Label" field IS this item's (required) menu
        // label — bound to the same `profiles.${languageId}.label` path a
        // dropdown/mega_menu item's dedicated Label field below writes to,
        // so there's never a duplicate label input for any type.
        <CtaEditor
          control={control}
          languageId={languageId}
          labelName={`${base}.profiles.${languageId}.label`}
          linkName={`${base}.external_url`}
          newTabName={`${base}.link_new_tab`}
          linkPageIdName={`${base}.link_page_id`}
          linkTypeName={`${base}.link_type`}
          linkCategoryIdName={`${base}.link_category_id`}
          linkCollectionIdName={`${base}.link_collection_id`}
        />
      ) : type === 'dropdown' ? (
        <>
          <InputFieldContainer
            label="Label"
            control={control}
            name={`${base}.profiles.${languageId}.label`}
            render={({ field }) => (
              <FormItem className="col-span-3">
                <FormControl>
                  <Input
                    placeholder="e.g., Sofas"
                    type="text"
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <NavLinkList
            form={form}
            base={base}
            droppableId={`nav-dropdown-links-${index}`}
            languageId={languageId}
            allLanguages={allLanguages}
          />
        </>
      ) : (
        <>
          <InputFieldContainer
            label="Label"
            control={control}
            name={`${base}.profiles.${languageId}.label`}
            render={({ field }) => (
              <FormItem className="col-span-3">
                <FormControl>
                  <Input
                    placeholder="e.g., Sofas"
                    type="text"
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {panelSource === 'categories' && (
            <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-sm text-blue-800">
              Columns are generated automatically from categories marked &quot;Show in menu&quot;.
            </div>
          )}

          {type === 'mega_menu' && panelSource !== 'manual' && (form.watch(`${base}.links`) ?? []).length > 0 && (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
              <p className="font-semibold">
                {(form.watch(`${base}.links`) ?? []).length} link
                {(form.watch(`${base}.links`) ?? []).length === 1 ? '' : 's'} will be discarded, not kept, when you
                save this item.
              </p>
              <p className="mt-1">
                This panel&apos;s manual link list is hidden because Panel source isn&apos;t &quot;Manual
                list&quot; &mdash; switch Panel source back to &quot;Manual list&quot;, or switch Type back to
                &quot;Dropdown&quot;, to keep{' '}
                {(form.watch(`${base}.links`) ?? []).length === 1 ? 'it' : 'them'}.
              </p>
            </div>
          )}

          <div className="grid grid-cols-4 items-start gap-4">
            <Label className="text-right pt-2">Panel source</Label>
            <div className="col-span-3 flex gap-4 pt-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={panelSource === 'categories'}
                  onChange={() => handlePanelSourceChange('categories')}
                />
                <span className="text-sm">Categories</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={panelSource === 'page'}
                  onChange={() => handlePanelSourceChange('page')}
                />
                <span className="text-sm">CMS page</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={panelSource === 'manual'}
                  onChange={() => handlePanelSourceChange('manual')}
                />
                <span className="text-sm">Manual list</span>
              </label>
            </div>
          </div>

          {panelSource === 'categories' && (
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-3">
                Category scope
                <p className="text-right text-xs pt-1 font-light text-muted-foreground">
                  Limit columns to children of&hellip; (optional)
                </p>
              </Label>
              <div className="col-span-3">
                <CategoryTreeSelect
                  value={panelRootCategoryField.value}
                  onChange={panelRootCategoryField.onChange}
                  placeholder="All top-level categories"
                />
              </div>
            </div>
          )}

          {panelSource === 'page' && (
            <InputFieldContainer
              label="Root page"
              description="Child pages of the selected page will appear as the panel's links."
              control={control}
              name={`${base}.panel_root_page_id`}
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <PageParentSelect
                      value={field.value ?? null}
                      onChange={val => field.onChange(val)}
                      placeholder="Select a page..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {panelSource === 'manual' && (
            <NavLinkList
              form={form}
              base={base}
              droppableId={`nav-mega-links-${index}`}
              languageId={languageId}
              allLanguages={allLanguages}
            />
          )}

          <InputFieldContainer
            label="Featured page"
            description="The card shows this page's hero image, title and tags."
            control={control}
            name={`${base}.featured_page_id`}
            render={({ field }) => (
              <FormItem className="col-span-3">
                <FormControl>
                  <PageParentSelect
                    value={field.value ?? null}
                    onChange={val => field.onChange(val)}
                    placeholder="No featured page"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  )
}
